<div class="intake-desk-page container-fluid">
  <div class="intake-desk-shell d-flex flex-column mx-auto">
    <section class="intake-desk-hero">
      <div class="intake-desk-breadcrumb text-uppercase d-flex align-items-center gap-2">
        <span>Intake</span>
        <span>/</span>
        <span>Desk</span>
      </div>
      <h1 class="intake-desk-title">Intake desk</h1>
    </section>

    <section class="row g-3 intake-desk-summary-row">
      <div v-for="metric in summary_metrics" :key="metric.label" class="col-12 col-md-6 col-xl-3">
        <article class="intake-desk-panel intake-desk-summary-card h-100 shadow">
          <div class="intake-desk-panel-kicker fw-bold">{{ metric.label }}</div>
          <div class="intake-desk-summary-value">{{ metric.value }}</div>
          <div class="intake-desk-summary-meta" :class="metric.meta_class">{{ metric.meta }}</div>
        </article>
      </div>
    </section>

    <section class="intake-queue-section">
      <div class="mb-3">
        <h2 class="intake-desk-section-title mb-2">Inbound queue</h2>
        <div class="intake-desk-section-copy">Raw requests waiting to be validated and registered</div>
      </div>

      <div class="intake-queue-toolbar">
        <div class="intake-queue-search-wrap shadow-sm">
          <input
            v-model.trim="search_term"
            type="text"
            class="form-control form-control-sm intake-queue-search text-xs rounded-small"
            placeholder="Search inbound - name, address, channel"
          >
        </div>

        <select
          id="intake_inbound_status_filter"
          class="company-cases-filter company-cases-filter-select shadow-sm"
          :value="active_inbound_status"
          @change="set_inbound_status_filter($event.target.value)"
        >
          <option
            v-for="status in display_inbound_status_filters"
            :key="status.key"
            :value="status.key"
          >
            {{ status.label }}
          </option>
        </select>

        <div class="intake-queue-filter-list">
          <button
            v-for="channel in channels"
            :key="channel.key"
            type="button"
            class="intake-queue-filter-btn shadow-sm"
            :class="{ 'is-active': active_channel === channel.key }"
            @click="set_channel(channel.key)"
          >
            {{ channel.label }}
          </button>
        </div>
      </div>

      <div class="company-cases-table-wrap shadow">
        <div class="table-responsive">
          <table class="table company-cases-table align-middle mb-0">
            <thead>
              <tr>
                <th>Inbound request</th>
                <th>Channel</th>
                <th>Suggested type</th>
                <th>Flags</th>
                <th class="text-end">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in filtered_inbound_requests" :key="item.id">
                <td>
                  <div class="company-case-title">{{ item.title }}</div>
                  <div class="company-case-subtitle">{{ item.detail }}</div>
                </td>
                <td>
                  <span class="company-case-domain" :class="item.channel_badge_class">{{ item.channel_label }}</span>
                </td>
                <td>
                  <span class="company-case-domain" :class="item.suggested_type_badge_class">{{ item.suggested_type }}</span>
                </td>
                <td>
                  <span class="company-case-sla" :class="item.flag_badge_class">
                    <span class="company-case-sla-text">{{ item.flag || '-' }}</span>
                  </span>
                </td>
                <td class="text-end">
                  <div class="company-case-actions justify-content-end">
                    <button type="button" class="company-case-view-btn" @click="open_register_case(item)">Register</button>
                  </div>
                </td>
              </tr>
              <tr v-if="!filtered_inbound_requests.length">
                <td colspan="5" class="py-4 text-center text-secondary">No inbound requests match this filter.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <section class="intake-main-db-section">
      <div class="d-flex flex-column flex-xl-row align-items-xl-center justify-content-between gap-3 mb-3">
        <div>
          <h2 class="intake-desk-section-title mb-2">Main case database - live</h2>
          <div class="intake-desk-section-copy">
            Registered cases and where they are in the flow. Triage, then route to a coordinator or straight to a handler.
          </div>
        </div>

        <select
          id="intake_live_case_status_filter"
          class="company-cases-filter company-cases-filter-select shadow-sm"
          :value="active_live_case_status"
          @change="set_live_case_status_filter($event.target.value)"
        >
          <option
            v-for="status in display_live_case_status_filters"
            :key="status.key"
            :value="status.key"
          >
            {{ status.label }}
          </option>
        </select>
      </div>

      <div class="company-cases-table-wrap shadow">
        <div class="table-responsive">
          <table class="table company-cases-table align-middle mb-0">
            <thead>
              <tr>
                <th>Case</th>
                <th>Type</th>
                <th>Stage</th>
                <th>Routed to</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in filtered_live_cases" :key="item.case_code" class="case-clickable-row" @click="go_to_case_detail(item)">
                <td>
                  <div class="company-case-code text-uppercase">{{ item.case_code }}</div>
                  <div class="company-case-title">{{ item.title }}</div>
                  <div class="company-case-subtitle">{{ item.detail }}</div>
                </td>
                <td>
                  <span class="company-case-domain" :class="item.type_badge_class">{{ item.type }}</span>
                </td>
                <td>
                  <div class="company-case-status d-flex align-items-center gap-2">
                <span class="status-dot-sm rounded-circle d-inline-block" :class="item.status_color ? '' : item.stage_dot_class" :style="item.status_dot_style"></span>
                    <span>{{ item.stage }}</span>
                  </div>
                </td>
                <td>
                  <div v-if="item.routed_to" class="company-case-owner">{{ item.routed_to }}</div>
                  <div v-else class="company-case-actions">
                    <button
                      type="button"
                      class="company-case-route-btn"
                      @click.stop="route_to_case(item)"
                    >
                      Route To
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="!filtered_live_cases.length">
                <td colspan="4" class="py-4 text-center text-secondary">No cases match this status filter.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>

  </div>

  <div
    v-if="show_manual_case_modal"
    class="company-case-modal-backdrop d-flex align-items-center justify-content-center"
    @click.self="close_manual_case_modal"
  >
    <section class="company-case-modal bg-white manual-intake-modal position-relative">
      <blocking-loader-overlay
        :show="submitting_manual_case"
        message="Registering case..."
        detail="Please wait while the case and its detail forms are saved."
      ></blocking-loader-overlay>
      <header class="company-case-modal-header d-flex align-items-start justify-content-between gap-3">
        <div>
          <h2 class="company-case-modal-title mb-2">{{ manual_case.title }}</h2>
          <div class="company-case-modal-copy">{{ manual_case.subtitle }}</div>
        </div>
        <button type="button" class="company-case-modal-close btn border-0 p-0" aria-label="Close modal" @click="close_manual_case_modal">
          <i class="bi bi-x-lg"></i>
        </button>
      </header>

      <div class="company-case-modal-body">
        <div v-if="manual_case_error" class="small text-danger fw-semibold mb-3" role="alert">
          {{ manual_case_error }}
        </div>
        <p v-if="manual_case.description" class="mb-4 company-case-modal-description">
          {{ manual_case.description }}
        </p>
        <div class="mb-3">
          <label class="company-case-modal-label text-uppercase mb-2" for="manual_intake_requester">Requester<span class="manual-required-marker">*</span></label>
          <input
            id="manual_intake_requester"
            v-model.trim="manual_case.requester"
            type="text"
            class="form-control company-case-modal-control"
            placeholder="Full name"
          >
        </div>

        <div class="mb-3">
          <label class="company-case-modal-label text-uppercase mb-2" for="manual_intake_contact">Contact</label>
          <input
            id="manual_intake_contact"
            v-model.trim="manual_case.contact"
            type="text"
            class="form-control company-case-modal-control"
            placeholder="Phone or email"
          >
        </div>

        <div class="mb-3">
          <label class="company-case-modal-label text-uppercase mb-2" for="manual_intake_location">Location / Address</label>
          <input
            id="manual_intake_location"
            v-model.trim="manual_case.location"
            type="text"
            class="form-control company-case-modal-control"
            placeholder="Address or area"
          >
        </div>

        <div class="mb-0">
          <label class="company-case-modal-label text-uppercase mb-2" for="manual_intake_case_type">Case type<span class="manual-required-marker">*</span></label>
          <select
            id="manual_intake_case_type"
            v-model="manual_case.case_type"
            class="form-select company-case-modal-control"
          >
            <option v-for="option in case_type_options" :key="option.id" :value="option.id">{{ option.label }}</option>
          </select>
        </div>
      </div>

      <footer class="company-case-modal-footer d-flex justify-content-end gap-2">
        <button type="button" class="btn btn-light border px-4" @click="close_manual_case_modal">
          Need more info
        </button>
        <button type="button" class="btn btn-primary-dark px-4" :disabled="!can_submit_manual_case" @click="submit_manual_case">
          Register -> onto main DB
        </button>
      </footer>
    </section>
  </div>

  <div v-if="show_route_case_modal" class="company-case-modal-backdrop d-flex align-items-center justify-content-center" @click.self="close_route_case_modal">
    <section class="company-case-modal company-case-route-modal bg-white position-relative">
      <blocking-loader-overlay
        :show="submitting_route_case"
        message="Saving route..."
        detail="Please wait while the case status and assignments are updated."
      ></blocking-loader-overlay>
      <header class="company-case-modal-header d-flex align-items-start justify-content-between gap-3">
        <div>
          <h2 class="company-case-modal-title mb-2">{{ route_case_title }}</h2>
          <div class="company-case-modal-copy">{{ route_case_subtitle }}</div>
        </div>
        <button type="button" class="company-case-modal-close btn border-0 p-0" aria-label="Close route modal" @click="close_route_case_modal">
          <i class="bi bi-x-lg"></i>
        </button>
      </header>

      <div class="company-case-modal-body company-case-route-body">
        <div class="company-case-modal-label text-uppercase mb-2">Priority</div>
        <div v-if="route_status_options.length">
          <label
            v-for="status in route_status_options"
            :key="status.id"
            class="case-details-decision-option company-case-route-option mb-2"
            :class="{ 'is-selected': String(route_priority) === String(status.id) }"
          >
            <input
              v-model="route_priority"
              class="case-details-decision-radio"
              type="radio"
              :value="String(status.id)"
              :disabled="route_loading || submitting_route_case"
            >
            <span class="case-details-decision-radio-ui"></span>
            <span>
              <span class="case-details-decision-option-title">
                {{ status.label }}
                <span v-if="status.is_final" class="case-details-decision-badge">Final</span>
              </span>
            </span>
          </label>
        </div>

        <div class="company-case-modal-label text-uppercase mb-2">Route to</div>
        <div v-if="route_destination_options.length">
          <label
            v-for="role in route_destination_options"
            :key="role.id"
            class="case-details-decision-option company-case-route-option mb-2"
            :class="{ 'is-selected': String(route_destination) === String(role.id) }"
          >
            <input
              v-model="route_destination"
              class="case-details-decision-radio"
              type="radio"
              :value="String(role.id)"
              :disabled="route_loading || submitting_route_case"
              @change="sync_route_group_selection"
            >
            <span class="case-details-decision-radio-ui"></span>
            <span>
              <span class="case-details-decision-option-title">{{ role.label }}</span>
              <span v-if="role.group_name" class="case-details-decision-option-body">{{ role.group_name }}</span>
            </span>
          </label>
        </div>

        <div v-if="selected_route_role" class="mt-3">
          <div class="company-case-modal-label text-uppercase mb-2">Role group</div>
          <select
            v-model="route_group"
            class="form-select company-case-modal-control"
            :disabled="route_loading || submitting_route_case || !available_route_groups.length"
            @change="sync_route_user_selection"
          >
            <option value="" disabled>Select a role group</option>
            <option v-for="group in available_route_groups" :key="group.id" :value="String(group.id)">
              {{ group.label }}
            </option>
          </select>
        </div>

        <div v-if="selected_route_group" class="mt-3">
          <div class="company-case-modal-label text-uppercase mb-2">Users</div>
          <label class="case-details-decision-option company-case-route-option mb-2" :class="{ 'is-selected': route_select_all_users }">
            <input
              v-model="route_select_all_users"
              class="case-details-decision-radio"
              type="checkbox"
              :disabled="route_loading || submitting_route_case || !available_route_users.length"
              @change="toggle_all_route_users"
            >
            <span class="case-details-decision-radio-ui"></span>
            <span class="case-details-decision-option-title">All users in this role group</span>
          </label>

          <label
            v-for="user in available_route_users"
            :key="user.user_id"
            class="case-details-decision-option company-case-route-option mb-2"
            :class="{ 'is-selected': route_selected_user_ids.includes(String(user.user_id)) }"
          >
            <input
              v-model="route_selected_user_ids"
              class="case-details-decision-radio"
              type="checkbox"
              :value="String(user.user_id)"
              :disabled="route_loading || submitting_route_case"
              @change="sync_route_user_selection"
            >
            <span class="case-details-decision-radio-ui"></span>
            <span>
              <span class="case-details-decision-option-title">{{ user.name }}</span>
              <span class="case-details-decision-option-body">{{ assignment_label(user) }}</span>
            </span>
          </label>

          <div v-if="!available_route_users.length" class="small text-secondary">
            No users were found for the selected role group.
          </div>
        </div>

        <div v-if="route_loading" class="small text-secondary mt-3">
          Loading routing options...
        </div>
        <div v-else-if="route_error" class="small text-danger fw-semibold mt-3">
          {{ route_error }}
        </div>
      </div>

      <footer class="company-case-modal-footer d-flex justify-content-end gap-2">
        <button type="button" class="btn btn-light border px-4" :disabled="submitting_route_case" @click="close_route_case_modal">Cancel</button>
        <button type="button" class="btn btn-primary-dark px-4" :disabled="!can_save_route_case" @click="route_selected_case">
          {{ submitting_route_case ? 'Routing...' : 'Route case ->' }}
        </button>
      </footer>
    </section>
  </div>
</div>
